import {
  arePromptVariablesResolved,
  cloneFormData,
} from '@/modules/templates/lib/dynamic-flow/utils';
import { generateDynamicFields, DynamicField } from '@/modules/templates/services/ai.service';

import { TemplateScreen } from '@/modules/templates/types/templates.types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDynamicForm } from '../DynamicFormContext';

export type DynamicFieldType = DynamicField;

// Cache types
type DynamicFieldsCache = Record<string, DynamicFieldType[]>;
type JurisdictionCache = Record<string, string | null>;

// Timeout for dynamic field generation
const DYNAMIC_FIELDS_FETCH_TIMEOUT = 90000;
// Timeout for waiting state
const DYNAMIC_SCREEN_WAITING_TIMEOUT = 45000;

export interface UseDynamicFieldsReturn {
  dynamicFieldsCache: DynamicFieldsCache;
  setDynamicFieldsCache: React.Dispatch<React.SetStateAction<DynamicFieldsCache>>;
  dynamicFieldsLoading: boolean;
  dynamicFieldsError: string | null;
  setDynamicFieldsError: (error: string | null) => void;
  dynamicFieldsFetchedRef: React.MutableRefObject<Set<string>>;
  jurisdictionCache: JurisdictionCache;
  failedDynamicScreens: Set<string>;
  prefetchingStepIndices: Set<number>;
}

export function useDynamicFields(): UseDynamicFieldsReturn {
  const { t } = useTranslation();
  const { config, formData, enrichmentContext, currentStep, totalSteps, nextStep } =
    useDynamicForm();

  const [dynamicFieldsCache, setDynamicFieldsCache] = useState<DynamicFieldsCache>({});
  const [dynamicFieldsLoading, setDynamicFieldsLoading] = useState(false);
  const [dynamicFieldsError, setDynamicFieldsError] = useState<string | null>(null);
  const dynamicFieldsFetchedRef = useRef<Set<string>>(new Set());
  const [jurisdictionCache, setJurisdictionCache] = useState<JurisdictionCache>({});
  const [failedDynamicScreens, setFailedDynamicScreens] = useState<Set<string>>(new Set());
  const [prefetchingStepIndices, setPrefetchingStepIndices] = useState<Set<number>>(new Set());

  const prefetchingRef = useRef<Set<string>>(new Set());
  const activeControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Cleanup all active controllers on unmount
  useEffect(() => {
    const controllers = activeControllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  // Pre-fetch dynamic fields as soon as all required variables are available
  useEffect(() => {
    if (!config?.screens) return;

    const dynamicScreens = config.screens
      .map((screen, index) => ({ screen, index }))
      .filter(({ screen }) => {
        const screenType = (screen as TemplateScreen).type;
        const dynamicPrompt = (screen as TemplateScreen).dynamicPrompt;
        return (
          screenType === 'dynamic' &&
          dynamicPrompt &&
          !dynamicFieldsCache[screen.id] &&
          !dynamicFieldsFetchedRef.current.has(screen.id) &&
          !prefetchingRef.current.has(screen.id)
        );
      });

    if (dynamicScreens.length === 0) return;

    const timeoutsThisRun: ReturnType<typeof setTimeout>[] = [];

    dynamicScreens.forEach(({ screen, index: stepIndex }) => {
      const dynamicMaxFields = (screen as TemplateScreen).dynamicMaxFields || 5;
      const dynamicPrompt = (screen as TemplateScreen).dynamicPrompt as string;

      if (
        arePromptVariablesResolved(
          dynamicPrompt,
          formData,
          enrichmentContext,
          config.screens,
          currentStep
        )
      ) {
        prefetchingRef.current.add(screen.id);
        setPrefetchingStepIndices((prev) => new Set([...prev, stepIndex]));

        const controller = new AbortController();
        activeControllersRef.current.set(screen.id, controller);

        const timeoutId = setTimeout(
          () => controller.abort('Client timeout exceeded'),
          DYNAMIC_FIELDS_FETCH_TIMEOUT
        );
        timeoutsThisRun.push(timeoutId);

        (async () => {
          try {
            // console.log('start generating dynamic filed');
            const result = await generateDynamicFields(
              dynamicPrompt,
              cloneFormData(formData),
              cloneFormData(enrichmentContext),
              {
                maxFields: dynamicMaxFields,
                screenTitle: screen.title,
                screenDescription: screen.description,
              },
              controller.signal
            );

            clearTimeout(timeoutId);
            activeControllersRef.current.delete(screen.id);

            dynamicFieldsFetchedRef.current.add(screen.id);
            prefetchingRef.current.delete(screen.id);
            setPrefetchingStepIndices((prev) => {
              const next = new Set(prev);
              next.delete(stepIndex);
              return next;
            });

            setDynamicFieldsCache((prev) => ({
              ...prev,
              [screen.id]: result.fields,
            }));

            if (result.jurisdictionName) {
              setJurisdictionCache((prev) => ({
                ...prev,
                [screen.id]: result.jurisdictionName,
              }));
            }
          } catch (error) {
            clearTimeout(timeoutId);
            activeControllersRef.current.delete(screen.id);

            if (controller.signal.aborted) {
              prefetchingRef.current.delete(screen.id);
              setPrefetchingStepIndices((prev) => {
                const next = new Set(prev);
                next.delete(stepIndex);
                return next;
              });
              return;
            }

            void error;
            setFailedDynamicScreens((prev) => new Set([...prev, screen.id]));
            prefetchingRef.current.delete(screen.id);
            setPrefetchingStepIndices((prev) => {
              const next = new Set(prev);
              next.delete(stepIndex);
              return next;
            });
          }
        })();
      }
    });

    return () => {
      timeoutsThisRun.forEach((id) => clearTimeout(id));
    };
  }, [config, formData, enrichmentContext, dynamicFieldsCache, currentStep]);

  // Fetch dynamic fields when entering a dynamic screen (fallback if not pre-fetched)
  useEffect(() => {
    const screen = config?.screens[currentStep];
    if (!screen) return;

    const screenType = (screen as TemplateScreen).type;
    const dynamicPrompt = (screen as TemplateScreen).dynamicPrompt;
    const dynamicMaxFields = (screen as TemplateScreen).dynamicMaxFields || 5;

    if (screenType !== 'dynamic' || !dynamicPrompt) return;
    if (dynamicFieldsCache[screen.id]) return;
    if (dynamicFieldsFetchedRef.current.has(screen.id)) return;
    if (prefetchingRef.current.has(screen.id)) return;

    dynamicFieldsFetchedRef.current.add(screen.id);

    const controller = new AbortController();
    const controllersRef = activeControllersRef.current;
    controllersRef.set(screen.id, controller);

    const timeoutId = setTimeout(
      () => controller.abort('Client timeout exceeded'),
      DYNAMIC_FIELDS_FETCH_TIMEOUT
    );

    const fetchDynamicFields = async () => {
      setDynamicFieldsLoading(true);
      setDynamicFieldsError(null);

      try {
        const result = await generateDynamicFields(
          dynamicPrompt,
          cloneFormData(formData),
          cloneFormData(enrichmentContext),
          {
            maxFields: dynamicMaxFields,
            screenTitle: screen.title,
            screenDescription: screen.description,
          },
          controller.signal
        );

        clearTimeout(timeoutId);
        controllersRef.delete(screen.id);

        setDynamicFieldsCache((prev) => ({
          ...prev,
          [screen.id]: result.fields,
        }));

        if (result.jurisdictionName) {
          setJurisdictionCache((prev) => ({
            ...prev,
            [screen.id]: result.jurisdictionName,
          }));
        }
      } catch (error) {
        clearTimeout(timeoutId);
        controllersRef.delete(screen.id);

        if (controller.signal.aborted) return;

        const isTimeout = error instanceof Error && error.name === 'AbortError';
        setFailedDynamicScreens((prev) => new Set([...prev, screen.id]));
        setDynamicFieldsError(
          isTimeout
            ? t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_TIMEOUTERROR')
            : error instanceof Error
              ? error.message
              : t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_FAILEDTOGENERATE')
        );
        dynamicFieldsFetchedRef.current.delete(screen.id);
      } finally {
        setDynamicFieldsLoading(false);
      }
    };

    fetchDynamicFields();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
      controllersRef.delete(screen.id);
    };
  }, [config, currentStep, formData, enrichmentContext, dynamicFieldsCache, t]);

  // Auto-advance when user is on a failed dynamic screen
  useEffect(() => {
    const screen = config?.screens[currentStep];
    if (!screen) return;

    const screenType = (screen as TemplateScreen).type;
    if (screenType !== 'dynamic') return;

    if (failedDynamicScreens.has(screen.id)) {
      setDynamicFieldsError(t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_AUTOPROCEEDFAILURE'));

      const autoAdvanceTimeout = setTimeout(() => {
        if (currentStep < totalSteps - 1) {
          nextStep();
        }
      }, 2000);

      return () => clearTimeout(autoAdvanceTimeout);
    }
  }, [currentStep, failedDynamicScreens, config, totalSteps, nextStep, t]);

  // Auto-advance when dynamic screen is stuck in "waiting" state
  useEffect(() => {
    const screen = config?.screens[currentStep];
    if (!screen) return;

    const screenType = (screen as TemplateScreen).type;
    if (screenType !== 'dynamic') return;

    const isWaiting =
      !dynamicFieldsLoading && !dynamicFieldsError && !dynamicFieldsCache[screen.id];

    if (!isWaiting) return;

    const waitingTimeout = setTimeout(() => {
      setDynamicFieldsError(t('EMPLOYMENTAGREEMENT_SMARTFLOW_DYNAMIC_AUTOPROCEEDTIMEOUT'));

      setTimeout(() => {
        if (currentStep < totalSteps - 1) {
          nextStep();
        }
      }, 1500);
    }, DYNAMIC_SCREEN_WAITING_TIMEOUT);

    return () => clearTimeout(waitingTimeout);
  }, [
    currentStep,
    config,
    dynamicFieldsLoading,
    dynamicFieldsError,
    dynamicFieldsCache,
    totalSteps,
    nextStep,
    t,
  ]);

  return {
    dynamicFieldsCache,
    setDynamicFieldsCache,
    dynamicFieldsLoading,
    dynamicFieldsError,
    setDynamicFieldsError,
    dynamicFieldsFetchedRef,
    jurisdictionCache,
    failedDynamicScreens,
    prefetchingStepIndices,
  };
}
